import {
  ProductAttribute,
  ProductAttributeKeys,
  ProductConfiguration,
  ProductConfigurationValue,
} from "@wttb/product-configurator";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { camelToSentenceCase } from "@/common/utils/transform-camel-case";

type ConfigSummaryOptions = {
  config: ProductConfiguration | null;
  attributes?: ProductAttribute[] | null;
};

export type ConfigSummaryItem = {
  value: string;
  key: string;
};

type ConfigSummary = {
  configSummary: ConfigSummaryItem[];
  spine?: ConfigSummaryItem;
};

const SPINE_ATTRIBUTE_KEY = "spine";
enum AttributeGroupEnum {
  Cover = "cover",
  Inner = "inner",
}

const attributeOrderMapper = {
  [AttributeGroupEnum.Cover]: [
    ProductAttributeKeys.CoverMaterialWeight,
    ProductAttributeKeys.CoverMaterial,
    ProductAttributeKeys.CoverLamination,
    ProductAttributeKeys.Cover,
  ],
  [AttributeGroupEnum.Inner]: [
    ProductAttributeKeys.InnerMaterialWeight,
    ProductAttributeKeys.InnerMaterial,
    ProductAttributeKeys.InnerColors,
    ProductAttributeKeys.Inner,
  ],
};

const isGroupAttribute = (attributeKey: string, group: AttributeGroupEnum) =>
  attributeOrderMapper[group].includes(attributeKey);

export const getProductConfigSummary = ({
  config,
  attributes,
  t,
}: ConfigSummaryOptions & { t: ReturnType<typeof useTranslations<string>> }): ConfigSummary => {
  if (!config || !attributes?.length) {
    return { configSummary: [], spine: {} as ConfigSummaryItem };
  }

  // First of all we group attributes to join cover and inner attributes
  const groupedAttributes = attributes.reduce(
    (acc, attribute) => {
      if (isGroupAttribute(attribute.key, AttributeGroupEnum.Cover)) {
        acc.cover.push(attribute);
      } else if (isGroupAttribute(attribute.key, AttributeGroupEnum.Inner)) {
        acc.inner.push(attribute);
      } else {
        acc.other.push(attribute);
      }
      return acc;
    },
    {
      cover: [] as ProductAttribute[],
      inner: [] as ProductAttribute[],
      other: [] as ProductAttribute[],
    },
  );

  // Then we sort cover, inner and others attributes and transform it
  const coverAttributes = transformPaperAttributes(
    groupedAttributes.cover,
    AttributeGroupEnum.Cover,
    config,
  );
  const innerAttributes = transformPaperAttributes(
    groupedAttributes.inner,
    AttributeGroupEnum.Inner,
    config,
  );

  let spine: ConfigSummaryItem | undefined;
  if (config[SPINE_ATTRIBUTE_KEY]) {
    const selectedValue = config[SPINE_ATTRIBUTE_KEY];
    if (typeof selectedValue === "number" && selectedValue !== 0) {
      spine = {
        key: t("text-spine-width"),
        value: selectedValue.toString() + "mm",
      };
    }
  }
  const otherAttributes = sortOtherAttributes(groupedAttributes.other)
    .map((attribute: ProductAttribute) => {
      const selectedValue = config[attribute.key];

      if (selectedValue === null)
        return {
          key: "",
          value: "",
        };

      // Custom sizeRun value
      if (
        typeof selectedValue === "object" &&
        !Array.isArray(selectedValue) &&
        attribute.key === ProductAttributeKeys.SizeRun
      ) {
        return {
          key: camelToSentenceCase(attribute.key),
          value: attribute.values.reduce((acc, attributeValue) => {
            if (selectedValue[attributeValue.id]) {
              acc += `${attributeValue.display}: ${selectedValue[attributeValue.id]} `;
            }
            return acc;
          }, ""),
        };
      }

      // Custom size value
      if (
        typeof selectedValue === "string" &&
        attribute.key === ProductAttributeKeys.Size &&
        /^\d+x\d+$/.test(selectedValue)
      )
        return {
          key: camelToSentenceCase(attribute.key),
          value: `${selectedValue}mm`,
        };

      // Quantity is a number so can return it as a string
      if (typeof selectedValue === "number") {
        const value = selectedValue.toString();
        if (attribute.key === ProductAttributeKeys.Quantity) {
          const versionsAttribute = groupedAttributes.other.find(
            ({ key }) => key === ProductAttributeKeys.Versions,
          );

          if (versionsAttribute) {
            const versions = config[versionsAttribute.key]?.toString();

            if (versions && +versions > 1) {
              return {
                key: camelToSentenceCase(attribute.key),
                value: `${versions} x ${value} (${+versions * +value})`,
              };
            }
          }

          return {
            key: camelToSentenceCase(attribute.key),
            value: "x" + value,
          };
        }

        if (attribute.key === ProductAttributeKeys.Versions) {
          // keep it empty to be filtered out
          return { key: camelToSentenceCase(attribute.key), value: +value > 1 ? value : "" };
        }

        let suffix;
        if (attribute.key === ProductAttributeKeys.Pages) {
          suffix = "\u00A0" + t("text-pages-with-cover");
        }
        return {
          key: camelToSentenceCase(attribute.key),
          value: suffix ? value + suffix : value,
        };
      }

      return transformProductAttribute(attribute, selectedValue);
    })
    .filter((item) => Boolean(item.value));

  // Finally we join all attributes
  const configSummary: ConfigSummaryItem[] = [
    ...otherAttributes,
    ...innerAttributes,
    ...coverAttributes,
  ];
  return { configSummary, spine };
};

export const useProductConfigSummary = ({ attributes, config }: ConfigSummaryOptions) => {
  const t = useTranslations("common");
  return useMemo(() => getProductConfigSummary({ attributes, config, t }), [config, attributes, t]);
};

export const transformProductAttribute = (
  attribute: ProductAttribute,
  selectedValue: ProductConfigurationValue,
): ConfigSummaryItem => {
  const values =
    attribute.values?.filter((val) =>
      Array.isArray(selectedValue)
        ? selectedValue.includes(val.id)
        : typeof selectedValue === "string"
          ? val[attribute.returnKey] === selectedValue
          : null,
    ) ?? [];

  const key = camelToSentenceCase(attribute.key);
  const value = values.reduce((acc, val) => acc + val.display + " | ", "").slice(0, -3);
  return {
    key,
    value: value === key ? "Yes" : value,
  };
};

const sortPaperAttributes = (attributes: ProductAttribute[], group: AttributeGroupEnum) => {
  const order = attributeOrderMapper[group];
  return [...attributes].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
};

const sortOtherAttributes = (attributes: ProductAttribute[]) => {
  return [...attributes].sort((a, b) =>
    a.key === ProductAttributeKeys.Quantity ? -1 : b.key === ProductAttributeKeys.Quantity ? 1 : 0,
  );
};

const transformPaperAttributes = (
  attributes: ProductAttribute[],
  group: AttributeGroupEnum,
  config: ProductConfiguration,
) => {
  return sortPaperAttributes(attributes, group)
    .map((attribute: ProductAttribute) => {
      const selectedValue = config[attribute.key];

      if (selectedValue === null) return {} as ConfigSummaryItem;

      return transformProductAttribute(attribute, selectedValue);
    })
    .filter((item) => Boolean(item.value));
};
